/**
 * Slices through a gridded 3D inversion model (EarthImager 3D 3.8.6 static, 3.8.7 dynamic slices).
 * A slice samples the model's cells on a regular 2D raster: normal to x, y or depth, or vertical along
 * a diagonal of the grid. Pure helpers (no three.js); components/VolumeSlices.js draws the result.
 */
import { modelTheta } from './voxel-select';

const num = Number.isFinite;
const MAX_PX = 400; // raster side cap: a slice never costs more than 400 x 400 lookups

export const SLICE_AXES = [
  { id: 'x', label: 'X' }, { id: 'y', label: 'Y' }, { id: 'z', label: 'Z' },
  { id: 'yx', label: 'Y~X' }, { id: 'y-x', label: 'Y~−X' }, { id: 'abs', label: 'Y~|X|' },
];
export const DEFAULT_SLICES = {
  mode: 'off', axis: 'z', count: 1, pos: 0.5, hideBlocks: true,
  dyn: { x: { on: false, pos: 0.5 }, y: { on: false, pos: 0.5 }, z: { on: false, pos: 0.5 } },
};

/** Fractions of the extent of `n` evenly spaced slices (one slice sits at `single`). */
export function evenPositions(n, single = 0.5) {
  const k = Math.max(1, Math.min(20, Math.round(n) || 1));
  return k === 1 ? [single] : Array.from({ length: k }, (_, i) => (i + 0.5) / k);
}

/** Dynamic-slice state with one axis toggled on or off (the x / y / z keys). */
export function toggleDynamic(state, axis) {
  if (!state.dyn[axis]) return state;
  return { ...state, mode: 'dynamic', dyn: { ...state.dyn, [axis]: { ...state.dyn[axis], on: !(state.mode === 'dynamic' && state.dyn[axis].on) } } };
}

function centresOf(values) {
  const v = Array.from(values).filter(num).sort((a, b) => a - b);
  if (!v.length) return [];
  const tol = ((v[v.length - 1] - v[0]) || 1) * 1e-7;
  const out = [v[0]];
  for (let i = 1; i < v.length; i++) if (v[i] - out[out.length - 1] > tol) out.push(v[i]);
  return out;
}

/** Cell edges from sorted centres: midpoints inside, `lo`/`hi` at the ends. */
function edgesOf(centres, lo, hi) {
  const n = centres.length;
  const e = new Array(n + 1);
  e[0] = lo; e[n] = hi;
  for (let i = 1; i < n; i++) e[i] = (centres[i - 1] + centres[i]) / 2;
  return e;
}

/** Index of the interval of sorted `edges` that holds v, -1 outside. */
function locate(edges, v) {
  const n = edges.length - 1;
  const tol = (edges[n] - edges[0]) * 1e-9;
  if (v < edges[0] - tol || v > edges[n] + tol) return -1;
  let a = 0, b = n - 1;
  while (a < b) {
    const m = (a + b + 1) >> 1;
    if (edges[m] <= v) a = m; else b = m - 1;
  }
  return a;
}

const stepOf = (centres, lo, hi) => {
  let s = Infinity;
  for (let i = 1; i < centres.length; i++) s = Math.min(s, centres[i] - centres[i - 1]);
  return Number.isFinite(s) && s > 0 ? s : (hi - lo) || 1;
};

/**
 * Lookup structure of a gridded model: centres, edges, bounds and step per model axis (x, y, d = depth)
 * and a map from grid index to cell index. Null when the cells are not on a grid.
 * @param {Array} cells valid cells ({x, y, depth, x0, x1, y0, y1, top, bottom})
 */
export function buildGrid(cells) {
  if (!cells?.length) return null;
  const xs = centresOf(cells.map((c) => c.x)), ys = centresOf(cells.map((c) => c.y)), ds = centresOf(cells.map((c) => c.depth));
  if (!xs.length || !ys.length || !ds.length) return null;
  const bound = (centres, lo, hi) => {
    const half = stepOf(centres, 0, 0) / 2;
    return [num(lo) ? lo : centres[0] - half, num(hi) ? hi : centres[centres.length - 1] + half];
  };
  const min = (f) => cells.reduce((m, c) => Math.min(m, f(c)), Infinity);
  const max = (f) => cells.reduce((m, c) => Math.max(m, f(c)), -Infinity);
  const bx = bound(xs, min((c) => Math.min(c.x0, c.x1)), max((c) => Math.max(c.x0, c.x1)));
  const by = bound(ys, min((c) => Math.min(c.y0, c.y1)), max((c) => Math.max(c.y0, c.y1)));
  const bd = bound(ds, min((c) => Math.min(c.top, c.bottom)), max((c) => Math.max(c.top, c.bottom)));
  const ax = { x: xs, y: ys, d: ds };
  const bounds = { x: bx, y: by, d: bd };
  const edges = { x: edgesOf(xs, bx[0], bx[1]), y: edgesOf(ys, by[0], by[1]), d: edgesOf(ds, bd[0], bd[1]) };
  const size = (f) => cells.reduce((m, c) => { const v = Math.abs(f(c)); return v > 0 ? Math.min(m, v) : m; }, Infinity);
  const pick = (extent, centres, b) => (Number.isFinite(extent) ? extent : stepOf(centres, ...b));
  const step = {
    x: pick(size((c) => c.x1 - c.x0), xs, bx), y: pick(size((c) => c.y1 - c.y0), ys, by), d: pick(size((c) => c.bottom - c.top), ds, bd),
  };
  const lookup = new Map();
  const nx = xs.length, ny = ys.length;
  const ex = edges.x, ey = edges.y, ed = edges.d;
  cells.forEach((c, i) => {
    const ix = locate(ex, c.x), iy = locate(ey, c.y), id = locate(ed, c.depth);
    if (ix >= 0 && iy >= 0 && id >= 0) lookup.set(ix + nx * (iy + ny * id), i);
  });
  return { ax, bounds, edges, step, lookup, nx, ny, nd: ds.length, cellAt(x, y, d) {
    const ix = locate(ex, x), iy = locate(ey, y), id = locate(ed, d);
    if (ix < 0 || iy < 0 || id < 0) return -1;
    const k = lookup.get(ix + nx * (iy + ny * id));
    return k === undefined ? -1 : k;
  } };
}

const px = (len, step) => Math.max(1, Math.min(MAX_PX, Math.round(len / step)));

/**
 * One slice normal to x, y or z (depth) at `position`, a fraction of the model's extent along that axis.
 * @returns {{ kind, coord, nu, nv, idx: Int32Array, corners: number[][], uRange, vRange, uLabel, vLabel, vDown } | null}
 *  idx is row-major (rows ascend in v; v is depth for vertical slices, y for plan), -1 where no cell;
 *  corners are the model (x, y, depth) of the raster's (u0,v0), (u1,v0), (u1,v1), (u0,v1) corners.
 */
export function sliceCells(cells, { axis, position = 0.5, grid = null }) {
  const g = grid || buildGrid(cells);
  if (!g || !['x', 'y', 'z'].includes(axis)) return null;
  const key = axis === 'z' ? 'd' : axis;
  const f = Math.max(0, Math.min(1, position));
  const coord = g.bounds[key][0] + f * (g.bounds[key][1] - g.bounds[key][0]);
  const [uKey, vKey] = axis === 'x' ? ['y', 'd'] : axis === 'y' ? ['x', 'd'] : ['x', 'y'];
  const uR = g.bounds[uKey], vR = g.bounds[vKey];
  const nu = px(uR[1] - uR[0], g.step[uKey]), nv = px(vR[1] - vR[0], g.step[vKey]);
  const idx = new Int32Array(nu * nv);
  const at = { x: 0, y: 0, d: 0 };
  at[key] = coord;
  for (let j = 0; j < nv; j++) {
    at[vKey] = vR[0] + ((j + 0.5) / nv) * (vR[1] - vR[0]);
    for (let i = 0; i < nu; i++) {
      at[uKey] = uR[0] + ((i + 0.5) / nu) * (uR[1] - uR[0]);
      idx[j * nu + i] = g.cellAt(at.x, at.y, at.d);
    }
  }
  const corner = (u, v) => { const p = { x: coord, y: coord, d: coord }; p[uKey] = u; p[vKey] = v; return [p.x, p.y, p.d]; };
  return {
    kind: axis, coord, nu, nv, idx, uRange: uR, vRange: vR,
    corners: [corner(uR[0], vR[0]), corner(uR[1], vR[0]), corner(uR[1], vR[1]), corner(uR[0], vR[1])],
    uLabel: uKey === 'x' ? 'x (m)' : 'y (m)', vLabel: vKey === 'd' ? 'depth (m)' : 'y (m)', vDown: vKey === 'd',
  };
}

/**
 * A vertical slice along a diagonal of the grid, sampled at the cell spacing: 'yx' (Y~X) runs from
 * (xmin, ymin) to (xmax, ymax), 'y-x' (Y~-X) from (xmin, ymax) to (xmax, ymin).
 */
export function diagonalSlice(cells, kind, grid = null) {
  const g = grid || buildGrid(cells);
  if (!g || !['yx', 'y-x'].includes(kind)) return null;
  const [x0, x1] = g.bounds.x, [y0, y1] = g.bounds.y, [d0, d1] = g.bounds.d;
  const A = kind === 'yx' ? [x0, y0] : [x0, y1], B = kind === 'yx' ? [x1, y1] : [x1, y0];
  const len = Math.hypot(B[0] - A[0], B[1] - A[1]);
  const nu = px(len, Math.min(g.step.x, g.step.y)), nv = px(d1 - d0, g.step.d);
  const idx = new Int32Array(nu * nv);
  for (let j = 0; j < nv; j++) {
    const d = d0 + ((j + 0.5) / nv) * (d1 - d0);
    for (let i = 0; i < nu; i++) {
      const s = (i + 0.5) / nu;
      idx[j * nu + i] = g.cellAt(A[0] + s * (B[0] - A[0]), A[1] + s * (B[1] - A[1]), d);
    }
  }
  return {
    kind, coord: null, nu, nv, idx, uRange: [0, len], vRange: [d0, d1],
    corners: [[A[0], A[1], d0], [B[0], B[1], d0], [B[0], B[1], d1], [A[0], A[1], d1]],
    uLabel: 'distance along the diagonal (m)', vLabel: 'depth (m)', vDown: true,
  };
}

/**
 * The slices the viewer draws for a slice state ({ mode, axis, count, pos, dyn }).
 * @returns {Array<{ id, axis, position, slice, draggable }>}
 */
export function slicesFor(cells, state, grid = null) {
  if (!state || state.mode === 'off') return [];
  const g = grid || buildGrid(cells);
  if (!g) return [];
  const out = [];
  const add = (id, axis, position, draggable) => {
    const slice = axis === 'yx' || axis === 'y-x' ? diagonalSlice(cells, axis, g) : sliceCells(cells, { axis, position, grid: g });
    if (slice) out.push({ id, axis, position, slice, draggable });
  };
  if (state.mode === 'dynamic') {
    ['x', 'y', 'z'].forEach((a) => { if (state.dyn[a]?.on) add(`dyn-${a}`, a, state.dyn[a].pos, true); });
  } else if (state.axis === 'abs') {
    add('diag-yx', 'yx', 0.5, false);
    add('diag-y-x', 'y-x', 0.5, false);
  } else if (state.axis === 'yx' || state.axis === 'y-x') {
    add(`diag-${state.axis}`, state.axis, 0.5, false);
  } else {
    evenPositions(state.count, state.pos).forEach((p, i) => add(`${state.axis}-${i}`, state.axis, p, false));
  }
  return out;
}

/**
 * Model (x, y, depth) -> scene (x east, y up, z north) for a selection's cells placed at `positions`
 * (voxel-select flatPositions): same rotation and anchor as the blocks. `axes` are the scene unit
 * vectors of model x, y and depth. With `ground` (columnSurface) each column sits at
 * surface - depth - lowest surface, like flatPositions; the frame is then `curved` (meshes subdivide).
 */
export function modelFrame(cells, positions, ground = null) {
  const th = modelTheta(cells, positions);
  const cs = Math.cos(th), sn = Math.sin(th);
  const a = cells[0];
  const p0 = [positions[0], positions[2]];
  let base = Infinity;
  if (ground) cells.forEach((c) => { if (typeof c.surface === 'number' && c.surface < base) base = c.surface; });
  return {
    curved: !!ground,
    map: (x, y, d) => {
      const dx = x - a.x, dy = y - a.y;
      const s = ground ? ground(x, y) : null;
      return [p0[0] + dx * cs - dy * sn, (s === null ? 0 : s - base) - d, p0[1] + dx * sn + dy * cs];
    },
    axes: { x: [cs, 0, sn], y: [-sn, 0, cs], z: [0, -1, 0] },
  };
}

/**
 * Ground elevation of a model's (x, y) column from the cells' own `surface` (models with topography),
 * as (x, y) => elevation | null. Null when no cell carries a surface; null per call off the grid.
 * Slices under the terrain drape are sampled by depth and placed at `ground - depth` per column.
 */
export function columnSurface(cells, grid) {
  if (!grid || !cells?.some((c) => typeof c.surface === 'number')) return null;
  const col = new Map();
  cells.forEach((c) => {
    if (typeof c.surface !== 'number') return;
    const ix = locate(grid.edges.x, c.x), iy = locate(grid.edges.y, c.y);
    if (ix >= 0 && iy >= 0 && !col.has(ix + grid.nx * iy)) col.set(ix + grid.nx * iy, c.surface);
  });
  return (x, y) => {
    const ix = locate(grid.edges.x, x), iy = locate(grid.edges.y, y);
    if (ix < 0 || iy < 0) return null;
    const v = col.get(ix + grid.nx * iy);
    return v === undefined ? null : v;
  };
}

/**
 * Triangle mesh of a slice through `map` (model x, y, depth -> scene [x, y, z]). By default one quad;
 * a draped slice is subdivided along u (and v for plan slices) so it follows the ground.
 * Vertices are row-major (rows ascend in v) with uv = (s, t) in 0..1 of the raster.
 */
export function sliceMesh(slice, map, { maxSeg = 1 } = {}) {
  const su = Math.max(1, Math.min(maxSeg, slice.nu));
  const sv = slice.vDown ? 1 : Math.max(1, Math.min(maxSeg, slice.nv));
  const [c0, c1, , c3] = slice.corners;
  const positions = new Float32Array((su + 1) * (sv + 1) * 3);
  const uvs = new Float32Array((su + 1) * (sv + 1) * 2);
  const indices = [];
  for (let j = 0; j <= sv; j++) {
    for (let i = 0; i <= su; i++) {
      const s = i / su, t = j / sv, k = j * (su + 1) + i;
      const m = [0, 1, 2].map((a) => c0[a] + s * (c1[a] - c0[a]) + t * (c3[a] - c0[a]));
      positions.set(map(m[0], m[1], m[2]), k * 3);
      uvs[k * 2] = s; uvs[k * 2 + 1] = t;
      if (i < su && j < sv) indices.push(k, k + 1, k + su + 2, k, k + su + 2, k + su + 1);
    }
  }
  return { positions, uvs, indices };
}

/**
 * RGBA pixels (nu x nv, row 0 = v0) of a slice: colours of the cells it crosses; cells outside the
 * visible range at `outsideOpacity`, cropped cells and holes transparent. With `sel.alpha` (opacity curve
 * x outside opacity, from selectVoxels) each pixel takes its cell's alpha instead.
 * @param {{ colors: Float32Array, visible: Uint8Array }} sel voxel selection (lib/voxel-select.js)
 */
export function slicePixels(slice, sel, outsideOpacity = 0) {
  const out = new Uint8ClampedArray(slice.nu * slice.nv * 4);
  const outside = Math.round(Math.max(0, Math.min(1, outsideOpacity)) * 255);
  for (let k = 0; k < slice.idx.length; k++) {
    const i = slice.idx[k];
    if (i < 0) continue;
    const vis = sel.visible ? sel.visible[i] : 1;
    const a = vis === 0 ? 0 : sel.alpha ? Math.round(Math.max(0, Math.min(1, sel.alpha[i])) * 255) : vis === 1 ? 255 : outside;
    if (!a) continue;
    out[k * 4] = Math.round(sel.colors[i * 3] * 255);
    out[k * 4 + 1] = Math.round(sel.colors[i * 3 + 1] * 255);
    out[k * 4 + 2] = Math.round(sel.colors[i * 3 + 2] * 255);
    out[k * 4 + 3] = a;
  }
  return out;
}

/** Cell index under a point of a slice given by (u, v) in 0..1 of its raster, -1 if none. */
export function slicePick(slice, u, v) {
  const i = Math.min(slice.nu - 1, Math.max(0, Math.floor(u * slice.nu)));
  const j = Math.min(slice.nv - 1, Math.max(0, Math.floor(v * slice.nv)));
  return slice.idx[j * slice.nu + i];
}

const fmt1 = (v) => String(Number(v.toFixed(1))).replace('-', '−');

/** "x 3.5, y 12.5, z −2.9 m · 266 Ω·m" for the block under a click. */
export function formatProbe(cell) {
  if (!cell) return '';
  const z = num(cell.z) ? cell.z : -cell.depth;
  const r = cell.resistivity;
  const res = r >= 100 ? Math.round(r) : Number(r.toPrecision(3));
  return `x ${fmt1(cell.x)}, y ${fmt1(cell.y)}, z ${fmt1(z)} m · ${res} Ω·m`;
}
